import { v4 as uuid } from 'uuid';

type Message = Record<string, unknown>;
type OnMessageFn = (message: Message) => void;

export type Subscribe = (topic: string, onMessage: OnMessageFn) => string;
export type Publish = (topic: string, message: Message) => void;

export class EventBridge {
  private subscriberOnMsg: Record<string, OnMessageFn> = {};

  private subscriberTopics: Record<string, string> = {};

  private topics: Record<string, string[]> = {};

  private persistedMessages: Record<string, Message> = {};

  constructor({ persistedTopics }: { persistedTopics?: string[] } = {}) {
    if (persistedTopics && !Array.isArray(persistedTopics)) {
      throw new Error('Persisted topics must be an array of topics.');
    }
    if (persistedTopics) {
      this.persistedMessages = persistedTopics.reduce(
        (acc: Record<string, Message>, cur: string) => {
          acc[cur] = {};
          return acc;
        },
        {}
      );
    }
  }

  // Subscribes to a topic and returns a subscription ID
  public subscribe(topic: string, onMessage: OnMessageFn): string {
    if (typeof topic !== 'string') throw new Error('Topic must be a string.');
    if (typeof onMessage !== 'function')
      throw new Error('onMessage must be a function.');
    const subID = uuid();
    this.topics[topic] = this.topics[topic] || [];
    this.topics[topic].push(subID);
    this.subscriberOnMsg[subID] = onMessage;
    this.subscriberTopics[subID] = topic;
    if (topic in this.persistedMessages) {
      onMessage(this.persistedMessages[topic]);
    }
    return subID;
  }

  // Publishes a message to a topic
  public publish(topic: string, message: Message) {
    if (typeof topic !== 'string') throw new Error('Topic must be a string.');
    if (typeof message !== 'object') {
      throw new Error('Message must be an object.');
    }
    this.topics[topic]?.forEach((id) => this.subscriberOnMsg[id](message));
    if (topic in this.persistedMessages) {
      this.persistedMessages[topic] = message;
    }
  }

  // Unsubscribes a subscription
  public unsubscribe(subscriptionId: string) {
    const topic = this.subscriberTopics[subscriptionId];
    if (topic) {
      this.topics[topic] = this.topics[topic].filter(
        (id) => id !== subscriptionId
      );
      delete this.subscriberOnMsg[subscriptionId];
      delete this.subscriberTopics[subscriptionId];
    }
  }
}

export default EventBridge;
